class TariffCode {
  static RULES = [
    {
      patterns: [
        /^(?<handle>PI-ZB.*-ARA)\s?(?<title>.*)$/, // industrial touch screen
        /^(?<handle>PI-NF.*XX)\s?(?<title>.*)$/, // open frame touch screen
        /(?<handle>(VIO|WIO|SIO)-W?\d{3,4}-?MX100)\s?(?<title>.*)/, // touch MX100
        /(?<handle>(WIO|SIO)-W?\d{3,4}-?[a-zA-Z0-9-]*)\s?(?<title>.*)/,
        /(?<handle>FIO-[a-zA-Z0-9-]*)\s?(?<title>.*)/,
      ],
      code: { ECCN: "EAR99", HTS: "8528.59.1500", ScheduleB: "85.28" },
    },
    {
      patterns: [
        /^(?<handle>ct|CT-\w{3}[a-zA-Z0-9-]{2})\s?(?<title>.*)$/, // motherboard
      ],
      code: { ECCN: "5A992.c", HTS: "8504.90.2000", ScheduleB: "8473.30.0002" },
    },
    {
      patterns: [
        /(?<handle>(JCO|RCO|ACO|BCO|VCO|WCO)-\d{3,4}-?[a-zA-Z0-9-]*)\s?(?<title>.*)/, // embedded
        /(?<handle>(VIO|AIO|HIO)-W?\d{3,4}-?(PC100|ADL)?.*)\s?(?<title>.*)/, // touch PC
      ],
      code: { ECCN: "5A992.c", HTS: "8471.50.0150", ScheduleB: "84.71" },
    },
    {
      patterns: [
        /(?<handle>KCO-\d{3,4}-?[a-zA-Z0-9-]*)\s?(?<title>.*)/, // rackmount
      ],
      code: { ECCN: "5A992.c", HTS: "8542.31.0001", ScheduleB: "84.71" },
    },
  ];

  static LABELS = {
    ScheduleB: "Schedule B",
  };

  /**
   * Resolve the tariff code for a given product title/handle.
   * Returns null if no rule matches.
   */
  static resolve(title) {
    if (!title) return null;

    const rule = TariffCode.RULES.find(({ patterns }) => patterns.some((pattern) => pattern.test(title)));
    return rule ? { ...rule.code } : null;
  }

  /**
   * Render a tariff code object as an HTML table, ready to inject.
   */
  static renderTable(code, heading) {
    const rows = Object.entries(code)
      .map(
        ([key, value]) => `
          <tr>
            <td>${TariffCode.LABELS[key] || key}</td>
            <td>${value}</td>
          </tr>`,
      )
      .join("");

    return `
      <h3 class="cat border-color-prime border-b-2">${heading}</h3>
      <table class="spec-option">
        <tbody>${rows}</tbody>
      </table>
    `;
  }
}

class ProductEBIO {
  static API_URL = "https://webapp.premioinc.com/api/google/datasheet/edgeboost-io/transpose";
  static TITLE_HANDLE = "title";
  static DATASHEET_HANDLE = "resource_Data Sheet";
  static SKIP_MAP = {
    "jco-1000-orn-b_2l" : ["ebio-oob-i"],
    "jco-1000-orn-c_2m12" : ["ebio-oob-i"]
  }

  constructor(productId) {
    this.productId = productId;
    this.productJson = null;
    this.modulesDef = [];
    this.products = [];
  }

  /** Read and parse the Shopify-style product JSON script tag. */
  loadProductJson() {
    const el = document.querySelector(`script#ProductJson-${this.productId}`);
    if (!el) return null;

    this.productJson = JSON.parse(el.textContent);
    return this.productJson;
  }

  /** Fetch the raw transposed datasheet data from the API. */
  async fetchData() {
    const response = await fetch(ProductEBIO.API_URL);
    const data = await response.json();
    return data;
  }

  /**
   * Transform the raw API rows into:
   *  - modulesDef: [{ handle, title, link }]
   *  - products:   [{ handle, modules: [handle, ...] }]
   */
  parseData(data) {
    const modulesDef = [];
    const products = [];

    // Build module definitions (titles) and attach datasheet links
    data.forEach((item) => {
      if (item.handle === ProductEBIO.TITLE_HANDLE) {
        Object.entries(item).forEach(([key, value]) => {
          if (key === "handle") return;
          modulesDef.push({ handle: key, title: value });
        });
      } else if (item.handle === ProductEBIO.DATASHEET_HANDLE) {
        Object.entries(item).forEach(([key, value]) => {
          if (key === "handle") return;
          const def = modulesDef.find((m) => m.handle === key);
          if (def) def.link = value;
        });
      }
    });

    // Build per-product module lists
    data
      .filter((item) => item.handle !== ProductEBIO.TITLE_HANDLE && item.handle !== ProductEBIO.DATASHEET_HANDLE)
      .forEach((product) => {
        const modules = Object.keys(product).filter((key) => key !== "handle" && product[key]);
        products.push({ handle: product.handle.toLowerCase(), modules });
      });

    this.modulesDef = modulesDef;
    this.products = products;
    return { modulesDef, products };
  }


  /** Find the product entry matching the current page's product handle. */
  findMatchingProduct() {
    if (!this.productJson) return null;
    return this.products.find((item) => this.productJson.handle.includes(item.handle));
  }

  /** Resolve a product's module handles into full { handle, title, link } defs. */
 resolveModules(product) {
   if (!product) return [];

   const pageHandle = this.productJson.handle.toLowerCase();
   const skipList = ProductEBIO.SKIP_MAP[pageHandle] || [];

   return product.modules
     .filter((moduleHandle) => !skipList.includes(moduleHandle.toLowerCase()))
     .map((moduleHandle) => this.modulesDef.find((def) => def.handle === moduleHandle))
     .filter(Boolean);
 }

  static linkTemplate({ handle, title, link }) {
    if (!handle || !title || !link) return "";

    const OLD_PREFIX = "https://premio.blob.core.windows.net/";
    const NEW_PREFIX = "https://cdn.premioinc.com/";

    const cdnLink = link.replace(OLD_PREFIX, NEW_PREFIX);

    return `
      <p class="module-link mb-1">
        <a href="${cdnLink}" target="_blank" rel="noopener">${title}</a>
      </p>
    `;
  }

  /** Create a new modules row at the bottom of the resource table, return the value <td>. */
  static ensureModulesCell() {
    const resource = document.querySelector("#resource");
    let table = document.querySelector("table.resource-table");

    if (!table) {
      table = document.createElement("table");
      table.classList.add("resource-table");
      table.appendChild(document.createElement("tbody"));
      resource.appendChild(table);
    }

    const tbody = table.querySelector("tbody");
    const row = document.createElement("tr");
    row.id = "resource_Modules";
    row.innerHTML = `
      <td class="resource-title">EDGEBoost I/O</td>
      <td class="resource-value"></td>
    `;
    tbody.appendChild(row);

    return row.querySelectorAll("td")[1];
  }

  /** Render resolved modules into the resource table. */
  static render(modules) {
    if (!modules.length) return;

    const cell = ProductEBIO.ensureModulesCell();
    cell.innerHTML = modules.map((m) => ProductEBIO.linkTemplate(m)).join("");
  }

  /** Full pipeline: load, fetch, parse, resolve, render. */
  async run() {
    if (!this.loadProductJson()) return;

    const data = await this.fetchData();
    if (!data) return;

    this.parseData(data);
    if (!this.modulesDef.length || !this.products.length) return;

    const product = this.findMatchingProduct();
    if (!product) return;

    const modules = this.resolveModules(product);
    ProductEBIO.render(modules);
  }
}

document.addEventListener("DOMContentLoaded", async function () {
  const title = document.querySelector("#product-title").innerText;
  const container = document.querySelector("#tariff-code");

  const tariffCode = TariffCode.resolve(title);
  if (tariffCode) {
    console.log('window labels tariffcode', window.labels.tariffCode)
    container.innerHTML = TariffCode.renderTable(tariffCode, window.labels.tariffCode || "Tariff Code");
  }

  const specTable = document.querySelectorAll("#specification table.spec-option td");
  specTable.forEach((node) => {
    let content = node.innerHTML.replace(/(?:<br>\s*){2,}/g, "<br>");
    if (node.innerHTML.includes("<ul")) {
      content = content.replace(/<br>/g, "");
    }
    node.innerHTML = content || "";
  });

  const resourceTable = document.querySelector("#resource");
  const productId = resourceTable?.dataset?.productId;
  if (!productId) return;

  await new ProductEBIO(productId).run();
});
